import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocale } from "@/hooks/useLocale";
import { tFormat } from "@/lib/i18n/t-format";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Camera, Loader2, Check, ZoomIn, Bug, Search, Sparkles, Trash2, X,
  Paintbrush, PenTool, Archive, Download,
  Image as ImageLucide, Layers,
  FolderOpen,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "@/hooks/useUserRole";
import { ImageLightbox } from "@/components/world/ImageLightbox";
import { MapSvgTracer } from "@/components/map/MapSvgTracer";
import {
  uploadImageToStorage, fetchWorldContext,
  generateBrief, generateWorldImage, entityTypeToImageType, imageTypeLabel,
  searchCodexForBrief,
  type BriefProgressEvent,
} from "@/lib/world-helpers";
import { logActivity } from "@/lib/activity-logger";
import { getSessionUser } from "@/lib/auth-session";
import {
  addGenerationTask, updateGenerationTask, completeGenerationTask,
  getGenerationTasks,
} from "@/lib/generation-task-store";
import {
  getReferenceImageCache, addToReferenceImageCache, clearReferenceImageCache,
  type CachedImage,
} from "@/lib/reference-image-cache";
import {
  loadDraftsForEntity, saveDraft, deleteDraft,
  type MediaDraft,
} from "@/lib/media-drafts";

import {
  ReferencesTab, ResearchTab, ArtDirectionTab, DrawTab, GenerateTab, DraftsTab,
  LibraryTab, defaultPromptForType, imageTypeContext,
} from "./imagegen";
import type { SidebarTab, MediaItem, ImageGenTabProps } from "./imagegen";

interface ImageGenerationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entity: {
    id: string;
    name: string;
    type: string;
    description: string;
    world_id: string;
    image_url?: string | null;
  };
  worldId: string;
  existingMedia?: MediaItem[];
  onImageGenerated?: (entityId: string, imageUrl: string) => void;
  onImageAccepted?: (imageUrl: string) => void;
  onImageRemoved?: () => void;
  currentImageUrl?: string | null;
  dialogTitle?: string;
  previewAspectRatio?: string;
}

const TAB_ITEMS: { id: SidebarTab; label: string; icon: React.ReactNode }[] = [
  { id: "library", label: "Library", icon: <FolderOpen className="w-4 h-4" /> },
  { id: "references", label: "References", icon: <ImageLucide className="w-4 h-4" /> },
  { id: "research", label: "Research", icon: <Search className="w-4 h-4" /> },
  { id: "art", label: "Art Direction", icon: <Paintbrush className="w-4 h-4" /> },
  { id: "draw", label: "Draw", icon: <PenTool className="w-4 h-4" /> },
  { id: "generate", label: "Generate", icon: <Sparkles className="w-4 h-4" /> },
  { id: "drafts", label: "Drafts", icon: <Archive className="w-4 h-4" /> },
];

export function ImageGenerationDialog({
  open, onOpenChange, entity, worldId, existingMedia = [], onImageGenerated,
  onImageAccepted, onImageRemoved, currentImageUrl, dialogTitle, previewAspectRatio,
}: ImageGenerationDialogProps) {
  const { t } = useLocale();
  const { isAdmin, isSuperAdmin } = useUserRole();
  const isAdminUser = isAdmin || isSuperAdmin;
  const { flags: featureFlags } = useFeatureFlags();

  useEffect(() => {
    if (open && !featureFlags.image_generation) {
      toast.error(t.img_gen_disabled);
      onOpenChange(false);
    }
  }, [open, featureFlags.image_generation]);

  const [activeTab, setActiveTab] = useState<SidebarTab>("references");
  const [generating, setGenerating] = useState(false);
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefText, setBriefText] = useState("");
  const [hasCachedBrief, setHasCachedBrief] = useState(false);
  const [seedImageUrl, setSeedImageUrl] = useState<string | null>(null);
  const [uploadingSeed, setUploadingSeed] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  const [stepResearchEnabled, setStepResearchEnabled] = useState(true);
  const [stepArtEnabled, setStepArtEnabled] = useState(true);
  const [stepReferencesEnabled, setStepReferencesEnabled] = useState(true);
  const [stepDrawEnabled, setStepDrawEnabled] = useState(true);

  const [webSearchEnabled, setWebSearchEnabled] = useState(false);
  const [webSearchLoading, setWebSearchLoading] = useState(false);
  const [webReferenceImages, setWebReferenceImages] = useState<string[]>([]);
  const [webReferenceThumbnails, setWebReferenceThumbnails] = useState<string[]>([]);
  const [searchQueriesUsed, setSearchQueriesUsed] = useState<string[]>([]);
  const [selectedRefImages, setSelectedRefImages] = useState<Set<string>>(new Set());
  const [webSearchKeywords, setWebSearchKeywords] = useState("");
  const [codexSearchKeywords, setCodexSearchKeywords] = useState("");
  const [codexPrompt, setCodexPrompt] = useState("");
  const [generationPrompt, setGenerationPrompt] = useState("");
  const [creativityLevel, setCreativityLevel] = useState(50);
  const [generationStep, setGenerationStep] = useState<string | null>(null);
  const [searchProgress, setSearchProgress] = useState<BriefProgressEvent | null>(null);
  const [generatedPreview, setGeneratedPreview] = useState<string | null>(null);
  const [zoomRefImage, setZoomRefImage] = useState<string | null>(null);
  const [showDebugDialog, setShowDebugDialog] = useState(false);
  const [debugData, setDebugData] = useState<any>(null);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<string>("google/gemini-2.5-flash-image");

  const [codexSearchEnabled, setCodexSearchEnabled] = useState(false);
  const [codexSnippets, setCodexSnippets] = useState<string[]>([]);
  const [codexEntityImages, setCodexEntityImages] = useState<string[]>([]);

  const [showCachePopup, setShowCachePopup] = useState(false);
  const [cachedImages, setCachedImages] = useState<CachedImage[]>([]);
  const [showSvgTracer, setShowSvgTracer] = useState(false);

  const [drawingDataUrl, setDrawingDataUrl] = useState<string | null>(null);
  const [drawingInitialImage, setDrawingInitialImage] = useState<string | null>(null);

  const [currentDraftId, setCurrentDraftId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<MediaDraft[]>([]);
  const [draftsLoaded, setDraftsLoaded] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const draftSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const imageType = entityTypeToImageType(entity.type);
  const imgLabel = imageTypeLabel(imageType);
  const briefTaskId = `brief-${entity.id}`;
  const imageTaskId = `image-${entity.id}`;

  // ── Lifecycle effects ──
  useEffect(() => { if (open) setCachedImages(getReferenceImageCache(worldId)); }, [open, worldId]);

  const restoreFromTaskStore = useCallback(() => {
    const tasks = getGenerationTasks();
    const briefTask = tasks.find(task => task.id === briefTaskId && task.status === "done" && task.results);
    if (briefTask?.results) {
      const r = briefTask.results;
      if (r.briefText) { setBriefText(r.briefText); setHasCachedBrief(true); }
      if (r.referenceImages?.length) setWebReferenceImages(r.referenceImages);
      if (r.referenceThumbnails?.length) setWebReferenceThumbnails(r.referenceThumbnails);
      if (r.searchQueriesUsed?.length) setSearchQueriesUsed(r.searchQueriesUsed);
      setSelectedRefImages(new Set());
      setWebSearchEnabled(true);
      return true;
    }
    return false;
  }, [briefTaskId]);

  // Track which entity this dialog was last configured for
  const lastEntityIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (open) {
      const isSameEntity = lastEntityIdRef.current === entity.id;
      lastEntityIdRef.current = entity.id;

      // Only reset transient generation state — preserve configuration, prompt, references, seed image
      setGenerationStep(null); setGenerationError(null);

      // Restore previous drawing as initial image if we have auto-saved data
      if (drawingDataUrl && !drawingInitialImage) {
        setDrawingInitialImage(drawingDataUrl);
      }

      // Only reset fully when switching to a different entity
      if (!isSameEntity) {
        setDebugData(null); setGeneratedPreview(null);
        setCodexSnippets([]); setCodexEntityImages([]);
        setActiveTab("references");
        setWebSearchKeywords(entity.name);
        setCodexSearchKeywords(entity.name);
        setGenerationPrompt(defaultPromptForType[imageType] || "");
        const existingImage = currentImageUrl || entity.image_url;
        setSeedImageUrl(existingImage || null);
        if (!restoreFromTaskStore()) { setWebReferenceImages([]); setWebReferenceThumbnails([]); setSearchQueriesUsed([]); }
      } else {
        // Same entity reopened — keep everything, just restore defaults if empty
        if (!webSearchKeywords) setWebSearchKeywords(entity.name);
        if (!codexSearchKeywords) setCodexSearchKeywords(entity.name);
        if (!generationPrompt) setGenerationPrompt(defaultPromptForType[imageType] || "");
        if (!seedImageUrl) {
          const existingImage = currentImageUrl || entity.image_url;
          setSeedImageUrl(existingImage || null);
        }
      }
    }
  }, [open]);

  useEffect(() => {
    if (open && entity.id && !draftsLoaded) {
      loadDraftsForEntity(worldId, entity.id).then(d => { setDrafts(d); setDraftsLoaded(true); });
    }
    if (!open) { setDraftsLoaded(false); setCurrentDraftId(null); }
  }, [open, entity.id, worldId, draftsLoaded]);

  // Auto-save draft
  const autoSaveDraft = useCallback(async () => {
    if (!open || generating || briefLoading) return;
    const user = await getSessionUser();
    if (!user) return;
    const hasContent = briefText.trim() || generationPrompt.trim() || webReferenceImages.length > 0 || drawingDataUrl || generatedPreview;
    if (!hasContent) return;
    setSavingDraft(true);
    const draft = await saveDraft({
      id: currentDraftId || undefined, world_id: worldId, entity_id: entity.id, created_by: user.id,
      image_type: imageType, entity_name: entity.name, web_search_keywords: webSearchKeywords,
      codex_search_keywords: codexSearchKeywords, codex_prompt: codexPrompt, brief_text: briefText,
      generation_prompt: generationPrompt, web_reference_images: webReferenceImages as any,
      web_reference_thumbnails: webReferenceThumbnails as any, codex_snippets: codexSnippets as any,
      codex_entity_images: codexEntityImages as any, selected_ref_images: Array.from(selectedRefImages) as any,
      search_queries_used: searchQueriesUsed as any, seed_image_url: seedImageUrl,
      drawing_data_url: drawingDataUrl, creativity_level: creativityLevel, selected_model: selectedModel,
      generated_image_url: generatedPreview, debug_data: debugData,
      status: generatedPreview ? (generatedPreview === currentImageUrl ? "accepted" : "generated") : "draft",
    });
    if (draft) { setCurrentDraftId(draft.id); setDrafts(prev => { const filtered = prev.filter(d => d.id !== draft.id); return [draft, ...filtered]; }); }
    setSavingDraft(false);
  }, [open, generating, briefLoading, currentDraftId, worldId, entity.id, imageType, entity.name,
      webSearchKeywords, codexSearchKeywords, codexPrompt, briefText, generationPrompt,
      webReferenceImages, webReferenceThumbnails, codexSnippets, codexEntityImages,
      selectedRefImages, searchQueriesUsed, seedImageUrl, drawingDataUrl, creativityLevel,
      selectedModel, generatedPreview, debugData, currentImageUrl]);

  useEffect(() => {
    if (!open) return;
    if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current);
    draftSaveTimer.current = setTimeout(() => autoSaveDraft(), 3000);
    return () => { if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current); };
  }, [briefText, generationPrompt, generatedPreview, webReferenceImages.length, drawingDataUrl, selectedModel]);

  // Clipboard paste
  const handleFileUpload = useCallback(async (file: File) => {
    if (!file.type.startsWith("image/")) { toast.error(t.img_only_images); return; }
    setUploadingSeed(true);
    try { const url = await uploadImageToStorage(file, worldId); setSeedImageUrl(url); toast.success(t.img_ref_uploaded); }
    catch (err: any) { toast.error(tFormat(t.img_upload_failed, { error: err.message })); }
    finally { setUploadingSeed(false); }
  }, [worldId, t]);

  const handleSeedUpload = (e: React.ChangeEvent<HTMLInputElement>) => { const file = e.target.files?.[0]; if (file) handleFileUpload(file); };

  const handlePaste = useCallback((e: ClipboardEvent) => {
    if (!open) return;
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) { if (item.type.startsWith("image/")) { e.preventDefault(); const file = item.getAsFile(); if (file) handleFileUpload(file); return; } }
  }, [open, handleFileUpload]);

  useEffect(() => { document.addEventListener("paste", handlePaste); return () => document.removeEventListener("paste", handlePaste); }, [handlePaste]);

  const toggleRefImage = (url: string) => {
    setSelectedRefImages(prev => {
      const next = new Set(prev);
      if (next.has(url)) { next.delete(url); if (seedImageUrl === url) setSeedImageUrl(null); }
      else if (next.size < 3) next.add(url);
      return next;
    });
  };

  const persistToCache = useCallback((webImages: string[], webThumbs: string[], codexImgs: string[], keyword: string) => {
    const now = Date.now();
    const images: CachedImage[] = [
      ...webImages.map((url, i) => ({ url, thumbnail: webThumbs[i], source: "web" as const, keyword, foundAt: now })),
      ...codexImgs.map((url) => ({ url, source: "codex" as const, keyword, foundAt: now })),
    ];
    addToReferenceImageCache(worldId, images);
    setCachedImages(getReferenceImageCache(worldId));
  }, [worldId]);

  const loadDraft = (draft: MediaDraft) => {
    setCurrentDraftId(draft.id); setBriefText(draft.brief_text || "");
    setGenerationPrompt(draft.generation_prompt || ""); setWebSearchKeywords(draft.web_search_keywords || entity.name);
    setCodexSearchKeywords(draft.codex_search_keywords || entity.name); setCodexPrompt(draft.codex_prompt || "");
    setWebReferenceImages((draft.web_reference_images as string[]) || []);
    setWebReferenceThumbnails((draft.web_reference_thumbnails as string[]) || []);
    setCodexSnippets((draft.codex_snippets as string[]) || []); setCodexEntityImages((draft.codex_entity_images as string[]) || []);
    setSelectedRefImages(new Set((draft.selected_ref_images as string[]) || []));
    setSearchQueriesUsed((draft.search_queries_used as string[]) || []);
    setSeedImageUrl(draft.seed_image_url || null); setDrawingDataUrl(draft.drawing_data_url || null);
    setCreativityLevel(draft.creativity_level || 50); setSelectedModel(draft.selected_model || "google/gemini-2.5-flash-image");
    setGeneratedPreview(draft.generated_image_url || null); setDebugData(draft.debug_data || null);
    setHasCachedBrief(!!(draft.brief_text?.trim())); setWebSearchEnabled(!!(draft.web_reference_images as string[])?.length);
    setCodexSearchEnabled(!!(draft.codex_snippets as string[])?.length);
    toast.success(t.img_draft_loaded);
  };

  const handleDeleteDraft = async (draftId: string) => {
    await deleteDraft(draftId); setDrafts(prev => prev.filter(d => d.id !== draftId));
    if (currentDraftId === draftId) setCurrentDraftId(null); toast.success(t.img_draft_deleted);
  };

  const saveDrawingToWorldMedia = async (dataUrl: string) => {
    try {
      const user = await getSessionUser();
      if (!user) return;
      const resp = await fetch(dataUrl); const blob = await resp.blob();
      const file = new File([blob], `drawing-${Date.now()}.png`, { type: "image/png" });
      const imageUrl = await uploadImageToStorage(file, worldId);
      await supabase.from("world_media").insert({ world_id: worldId, entity_id: entity.id || null, type: "scene", title: `Drawing — ${entity.name}`, description: "Hand-drawn in Media Studio", image_url: imageUrl, prompt_used: "hand-drawn", created_by: user.id });
      toast.success(t.img_drawing_saved);
    } catch (err: any) { toast.error(tFormat(t.img_drawing_save_failed, { error: err.message })); }
  };

  // ── Brief & Search ──
  const fetchBrief = async (useWebSearch = false, isSearchMore = false) => {
    setBriefLoading(true); setSearchProgress(null);
    if (useWebSearch) setWebSearchLoading(true);
    addGenerationTask({ id: briefTaskId, entityName: entity.name, entityId: entity.id, worldId: entity.world_id, phase: useWebSearch ? "search" : "brief" });
    try {
      const worldContext = await fetchWorldContext(entity.world_id);
      let codexContext = ""; let newCodexImages: string[] = [];
      if (codexSearchEnabled) {
        setSearchProgress({ step: "codex_search", detail: "Searching world codex for lore details..." });
        const codexResult = await searchCodexForBrief(worldId, codexSearchKeywords.trim() || entity.name);
        setCodexSnippets(codexResult.snippets); setCodexEntityImages(codexResult.entityImages); newCodexImages = codexResult.entityImages;
        if (codexResult.snippets.length > 0) codexContext = codexResult.snippets.slice(0, 6).join("\n\n").substring(0, 2000);
      }
      let enrichedSubject = `${entity.name}: ${entity.description}`;
      const purposePrefix = imageTypeContext[imageType] || "";
      if (purposePrefix) enrichedSubject = `${purposePrefix}\n\n${enrichedSubject}`;
      if (generationPrompt.trim()) enrichedSubject += `\n\nVISUAL DIRECTION: ${generationPrompt.trim()}`;
      if (codexPrompt.trim()) enrichedSubject = `CODEX GUIDANCE: ${codexPrompt.trim()}\n\n${enrichedSubject}`;
      enrichedSubject += "\n\nIMPORTANT: Do NOT include any text, words, letters, numbers, labels, captions, watermarks, or typography in the image. The image must be purely visual with no readable text of any kind.";
      if (codexContext) enrichedSubject = `CODEX LORE:\n${codexContext}\n\n${enrichedSubject}`;
      const result = await generateBrief({
        subject: enrichedSubject, entityName: entity.name, type: imageType, worldContext,
        webSearch: useWebSearch, searchKeywords: webSearchKeywords.trim() || undefined, maxSerpQueries: 1, skipQueries: isSearchMore ? searchQueriesUsed : [],
      }, useWebSearch ? (event) => { setSearchProgress(event); updateGenerationTask(briefTaskId, { phase: event.step === "generating_brief" ? "brief" : "search", detail: event.detail, provider: event.provider, query: event.query }); } : undefined);
      if (isSearchMore) {
        setBriefText(prev => { const nb = result.brief || ""; return prev && nb ? `${prev}\n\n${nb}` : nb || prev; });
        if (result.reference_images?.length) { setWebReferenceImages(prev => [...new Set([...prev, ...result.reference_images!])].slice(0, 10)); setWebReferenceThumbnails(prev => [...prev, ...(result.reference_thumbnails || result.reference_images!)].slice(0, 10)); }
        setSearchQueriesUsed(prev => [...prev, ...(result.search_queries || [])]);
      } else {
        setBriefText(result.brief || `${entity.name}: ${entity.description}`);
        if (result.reference_images?.length) { setWebReferenceImages(result.reference_images.slice(0, 5)); setWebReferenceThumbnails((result.reference_thumbnails || result.reference_images).slice(0, 5)); setSelectedRefImages(new Set()); }
        setSearchQueriesUsed(result.search_queries || []);
      }
      setHasCachedBrief(true);
      setDebugData({ queries: isSearchMore ? [...(debugData?.queries || []), ...(result.search_queries || [])] : (result.search_queries || []), brief: result.brief || "", refCount: result.reference_images?.length || 0, imageType, model: selectedModel, seedImageUrl, generationPrompt, timestamp: new Date().toISOString() });
      completeGenerationTask(briefTaskId);
      persistToCache(result.reference_images || [], result.reference_thumbnails || result.reference_images || [], newCodexImages, webSearchKeywords.trim() || codexSearchKeywords.trim() || entity.name);
    } catch (err: any) {
      if (!isSearchMore) setBriefText(`${entity.name}: ${entity.description}`);
      setHasCachedBrief(true); completeGenerationTask(briefTaskId, err.message || "Brief failed");
    } finally { setBriefLoading(false); setWebSearchLoading(false); setSearchProgress(null); }
  };

  // ── Generate ──
  const handleGenerate = async () => {
    setGenerating(true); setGenerationStep("Preparing generation..."); setGeneratedPreview(null); setGenerationError(null);
    addGenerationTask({ id: imageTaskId, entityName: entity.name, entityId: entity.id, worldId: entity.world_id, phase: "image", detail: `Generating ${imgLabel.toLowerCase()}...` });
    try {
      const selectedRefs = stepReferencesEnabled ? Array.from(selectedRefImages).filter(u => u !== seedImageUrl) : [];
      setGenerationStep(selectedRefs.length > 0 ? `Generating with brief + ${selectedRefs.length} reference(s)...` : `Generating ${imgLabel.toLowerCase()} from brief...`);
      const effectiveSeed = (stepDrawEnabled ? drawingDataUrl : null) || (stepReferencesEnabled ? seedImageUrl : null) || undefined;
      const effectiveBrief = stepArtEnabled ? briefText : "";
      const descriptionToSend = (effectiveBrief || `${entity.name}: ${entity.description}`) + "\n\nIMPORTANT: Do NOT include any text, words, letters, numbers, labels, captions, watermarks, or typography in the image.";
      const result = await generateWorldImage({ worldId: entity.world_id, type: imageType, title: entity.name, description: descriptionToSend, entityId: entity.id, seedImageUrl: effectiveSeed, creativityLevel: effectiveSeed ? creativityLevel : undefined, additionalReferenceUrls: selectedRefs.length > 0 ? selectedRefs : undefined, precomputedBrief: effectiveBrief || undefined, model: selectedModel });
      setGeneratedPreview(result.image_url); setGenerationStep(null);
      logActivity({ action: `Generated ${imgLabel} for "${entity.name}"`, category: "image_gen", source: "ImageGenerationDialog", details: { entity_id: entity.id, type: imageType } });
      toast.success(t.img_generated); completeGenerationTask(imageTaskId);
    } catch (err: any) {
      setGenerationStep(null); const errorMsg = err.message || "Generation failed";
      if (errorMsg.includes("didn't produce") || errorMsg.includes("timeout") || errorMsg.includes("Rate limited") || errorMsg.includes("after retries")) setGenerationError(errorMsg);
      else toast.error(errorMsg);
      completeGenerationTask(imageTaskId, errorMsg);
    } finally { setGenerating(false); }
  };

  // ── Ref image grid renderer ──
  const renderRefImageGrid = (images: string[], thumbnails: string[], sourceBadge?: React.ReactNode) => (
    <div className="grid grid-cols-4 gap-1.5">
      {images.map((url, i) => {
        const isSelected = selectedRefImages.has(url);
        const displayUrl = thumbnails[i] || url;
        return (
          <div key={i} className={cn("relative aspect-square rounded-md overflow-hidden border-2 cursor-pointer transition-all group/ref", isSelected ? "border-primary ring-1 ring-primary/30" : "border-border opacity-70 hover:opacity-100 hover:border-primary/50")}>
            <img src={displayUrl} alt={`Reference ${i + 1}`} className="w-full h-full object-cover" loading="lazy" onError={(e) => { const el = e.target as HTMLImageElement; if (el.src !== url && url !== displayUrl) el.src = url; else el.style.display = "none"; }} />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/40 opacity-0 group-hover/ref:opacity-100 transition-opacity">
              <button className="p-1 rounded bg-background/80 hover:bg-background text-foreground" onClick={(e) => { e.stopPropagation(); setZoomRefImage(displayUrl); }}><ZoomIn className="w-3.5 h-3.5" /></button>
              <button className={cn("p-1 rounded text-xs font-medium", isSelected ? "bg-primary text-primary-foreground" : "bg-background/80 hover:bg-background text-foreground")} onClick={(e) => { e.stopPropagation(); toggleRefImage(url); }}><Check className="w-3.5 h-3.5" /></button>
            </div>
            {isSelected && <div className="absolute top-0.5 right-0.5 bg-primary rounded-full p-0.5"><Check className="w-2.5 h-2.5 text-primary-foreground" /></div>}
            {sourceBadge && <div className="absolute bottom-0.5 left-0.5">{sourceBadge}</div>}
          </div>
        );
      })}
    </div>
  );

  const refCount = selectedRefImages.size + (seedImageUrl ? 1 : 0);

  // ── Tab props ──
  const tabProps: ImageGenTabProps = {
    entity, worldId, imageType, imageLabel: imgLabel,
    briefText, setBriefText, hasCachedBrief, setHasCachedBrief, briefLoading, generating,
    webSearchEnabled, setWebSearchEnabled, webSearchKeywords, setWebSearchKeywords,
    webReferenceImages, webReferenceThumbnails, searchQueriesUsed,
    codexSearchEnabled, setCodexSearchEnabled, codexSearchKeywords, setCodexSearchKeywords,
    codexPrompt, setCodexPrompt, codexSnippets, codexEntityImages,
    generationPrompt, setGenerationPrompt, selectedModel, setSelectedModel,
    seedImageUrl, setSeedImageUrl, selectedRefImages, toggleRefImage,
    creativityLevel, setCreativityLevel, drawingDataUrl, setDrawingDataUrl,
    generatedPreview, setGeneratedPreview, generationStep, generationError,
    stepReferencesEnabled, stepResearchEnabled, stepArtEnabled, stepDrawEnabled,
    fetchBrief, handleGenerate, handleFileUpload, handleSeedUpload,
    existingMedia, currentImageUrl, onImageAccepted, onImageGenerated, onImageRemoved, onOpenChange,
    dialogTitle, previewAspectRatio, uploadingSeed, showMediaPicker, setShowMediaPicker, setActiveTab,
    drawingInitialImage, setDrawingInitialImage, saveDrawingToWorldMedia,
    isAdminUser, debugData, setShowDebugDialog, searchProgress, setZoomRefImage,
    cachedImages, setShowCachePopup,
    drafts, currentDraftId, savingDraft, loadDraft, handleDeleteDraft,
    renderRefImageGrid,
  };

  // ── Render ──
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[70rem] max-h-[92vh] flex flex-col overflow-hidden p-0" onClick={(e) => e.stopPropagation()}>
          <div className="px-5 pt-4 pb-3 border-b border-border shrink-0">
            <DialogTitle className="flex items-center gap-2 text-sm">
              <Sparkles className="w-4 h-4 text-primary" />
              {dialogTitle || `Media Studio: ${entity.name}`}
              <Badge variant="outline" className="text-[9px] ml-2">{imgLabel}</Badge>
              {savingDraft && <span className="text-[10px] text-muted-foreground ml-auto">auto-saving…</span>}
              {currentDraftId && !savingDraft && <span className="text-[10px] text-muted-foreground ml-auto">draft saved</span>}
            </DialogTitle>
          </div>

          <div className="flex flex-1 min-h-0 overflow-hidden">
            {/* Sidebar tabs */}
            <div className="w-56 shrink-0 border-r border-border bg-muted/10 flex flex-col py-4 gap-2 px-3">
              <div className="px-2 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Pipeline</div>
              {TAB_ITEMS.map(tab => {
                const isActive = activeTab === tab.id;
                const isToggleable = ["references", "research", "art", "draw"].includes(tab.id);
                const isStepEnabled = tab.id === "references" ? stepReferencesEnabled : tab.id === "research" ? stepResearchEnabled : tab.id === "art" ? stepArtEnabled : tab.id === "draw" ? stepDrawEnabled : true;
                const hasContent = tab.id === "references" ? (refCount > 0 || webReferenceImages.length > 0 || drawingDataUrl) : tab.id === "research" ? hasCachedBrief : tab.id === "art" ? (generationPrompt.trim() || briefText.trim()) : tab.id === "draw" ? !!drawingDataUrl : tab.id === "generate" ? !!generatedPreview : tab.id === "drafts" ? drafts.length > 0 : false;
                
                return (
                  <div key={tab.id} className="relative flex flex-col group">
                    <button 
                      onClick={() => setActiveTab(tab.id)} 
                      className={cn(
                        "flex items-center gap-3 px-3 py-3 rounded-xl w-full transition-all text-sm font-medium border text-left", 
                        !isStepEnabled && isToggleable ? "opacity-60" : "opacity-100", 
                        isActive 
                          ? "bg-background border-border shadow-sm text-foreground ring-1 ring-border/50" 
                          : hasContent 
                            ? "bg-primary/5 border-transparent text-primary hover:bg-primary/10" 
                            : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50"
                      )} 
                      title={tab.label}
                    >
                      <div className={cn("p-1.5 rounded-md", isActive ? "bg-primary/10 text-primary" : "bg-transparent")}>
                        {tab.icon}
                      </div>
                      <span className="flex-1 leading-tight">{tab.label}</span>
                      {hasContent && !isActive && isStepEnabled && <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />}
                    </button>
                    
                    {isToggleable && (
                      <div className={cn(
                        "absolute right-2 top-1/2 -translate-y-1/2 transition-opacity z-10",
                        "opacity-100"
                      )} onClick={(e) => { 
                          e.stopPropagation(); 
                          if (tab.id === "references") setStepReferencesEnabled(v => !v); 
                          else if (tab.id === "research") setStepResearchEnabled(v => !v); 
                          else if (tab.id === "art") setStepArtEnabled(v => !v); 
                          else if (tab.id === "draw") setStepDrawEnabled(v => !v); 
                      }} title={isStepEnabled ? "Disable step" : "Enable step"}>
                        <Switch checked={isStepEnabled} className="scale-75 data-[state=unchecked]:bg-muted-foreground/30 ring-0 shadow-none border-0 m-0" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 overflow-y-auto">
              <div className="p-4">
                {activeTab === "references" && <ReferencesTab {...tabProps} />}
                {activeTab === "research" && <ResearchTab {...tabProps} />}
                {activeTab === "art" && <ArtDirectionTab {...tabProps} />}
                {activeTab === "draw" && <DrawTab {...tabProps} />}
                {activeTab === "generate" && <GenerateTab {...tabProps} />}
                {activeTab === "drafts" && <DraftsTab {...tabProps} />}
                {activeTab === "library" && <LibraryTab {...tabProps} />}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ImageLightbox open={!!zoomRefImage} onOpenChange={(o) => !o && setZoomRefImage(null)} imageUrl={zoomRefImage} title="Reference Image"
        extraActions={<Button variant="outline" size="sm" className="text-xs h-7 gap-1" onClick={() => { const origUrl = webReferenceImages[webReferenceThumbnails.indexOf(zoomRefImage!)] || zoomRefImage!; toggleRefImage(origUrl); }}><Check className="w-3 h-3" />{zoomRefImage && selectedRefImages.has(webReferenceImages[webReferenceThumbnails.indexOf(zoomRefImage)] || zoomRefImage) ? "Deselect" : "Select"}</Button>} />

      {/* Cache Dialog */}
      <Dialog open={showCachePopup} onOpenChange={setShowCachePopup}>
        <DialogContent className="max-w-[70rem] max-h-[92vh] flex flex-col overflow-hidden p-0" onClick={(e) => e.stopPropagation()}>
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-sm"><Archive className="w-4 h-4 text-muted-foreground" /> Last Found Images ({cachedImages.length})</DialogTitle></DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-4">
              {(() => {
                const grouped = new Map<string, CachedImage[]>();
                for (const img of cachedImages) { const key = img.keyword || "unknown"; if (!grouped.has(key)) grouped.set(key, []); grouped.get(key)!.push(img); }
                return Array.from(grouped.entries()).map(([keyword, images]) => (
                  <div key={keyword} className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground flex items-center gap-1"><Search className="w-3 h-3" /> "{keyword}"<Badge variant="secondary" className="text-[9px] h-4 px-1.5 ml-1">{images.length}</Badge></label>
                    <div className="grid grid-cols-5 gap-1.5">
                      {images.map((img, i) => {
                        const isSelected = selectedRefImages.has(img.url);
                        {/* W22: keyboard-operable ref image toggle */}
                        return (<div key={i} role="button" tabIndex={0} aria-pressed={isSelected} className={cn("relative aspect-square rounded-md overflow-hidden border-2 cursor-pointer transition-all group/ref", isSelected ? "border-primary ring-1 ring-primary/30" : "border-border opacity-70 hover:opacity-100 hover:border-primary/50")} onClick={() => toggleRefImage(img.url)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleRefImage(img.url); } }}>
                          <img src={img.thumbnail || img.url} alt={`Cached ${i + 1}`} className="w-full h-full object-cover" loading="lazy" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                          {isSelected && <div className="absolute top-0.5 right-0.5 bg-primary rounded-full p-0.5"><Check className="w-2.5 h-2.5 text-primary-foreground" /></div>}
                        </div>);
                      })}
                    </div>
                  </div>
                ));
              })()}
            </div>
          </ScrollArea>
          <div className="flex justify-between pt-2">
            <Button variant="ghost" size="sm" className="text-xs gap-1 text-destructive" onClick={() => { clearReferenceImageCache(worldId); setCachedImages([]); }}><Trash2 className="w-3 h-3" /> Clear Cache</Button>
            <Button variant="default" size="sm" className="text-xs" onClick={() => setShowCachePopup(false)}>Done</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Debug Dialog */}
      <Dialog open={showDebugDialog} onOpenChange={setShowDebugDialog}>
        <DialogContent className="max-w-[70rem] max-h-[92vh] flex flex-col overflow-hidden p-0" onClick={(e) => e.stopPropagation()}>
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-sm"><Bug className="w-4 h-4" /> Debug Log</DialogTitle></DialogHeader>
          {debugData && <ScrollArea className="max-h-[60vh]"><pre className="text-[10px] bg-muted/30 rounded p-3 whitespace-pre-wrap break-all font-mono">{JSON.stringify(debugData, null, 2)}</pre></ScrollArea>}
          <Button variant="outline" size="sm" className="w-full gap-1.5 text-xs" onClick={() => { if (debugData) { const blob = new Blob([JSON.stringify(debugData, null, 2)], { type: "text/plain" }); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `debug-${entity.name.replace(/\s+/g, "-")}-${Date.now()}.txt`; a.click(); URL.revokeObjectURL(url); } }}><Download className="w-3 h-3" /> Download Full Debug Log</Button>
        </DialogContent>
      </Dialog>

      {/* SVG Tracer */}
      {showSvgTracer && generatedPreview && <MapSvgTracer open={showSvgTracer} onOpenChange={setShowSvgTracer} sourceImageUrl={generatedPreview} sourceTitle={entity.name} worldId={worldId} />}
    </>
  );
}
